import type { HtmlEditorHeading, VisualEditorActiveLink, VisualEditorAlign, VisualEditorCellEdge, VisualEditorSelectionStyle } from '../editor-types';
import { CELL_BORDER_EDGES, HTML_EDITOR_FONT_FAMILIES, HTML_EDITOR_HEADINGS } from '../editor-types';

const ALIGN_VALUES = new Set<VisualEditorAlign>(['left', 'center', 'right', 'justify']);

const normalizeFamilyToken = (value: string): string =>
    value
        .trim()
        .replace(/^["']|["']$/g, '')
        .toLowerCase();

const FONT_FAMILY_BY_PRIMARY = new Map(HTML_EDITOR_FONT_FAMILIES.map((family) => [normalizeFamilyToken(family.value.split(',')[0]), family.value]));

export const elementFromSelectionNode = (node: Node | null): Element | null => {
    if (!node) return null;
    return node instanceof Element ? node : node.parentElement;
};

export const closestFromNode = (node: Node | null, selector: string): Element | null => {
    return elementFromSelectionNode(node)?.closest(selector) ?? null;
};

export const getNearestTable = (node: Node | null): HTMLTableElement | null => {
    const table = closestFromNode(node, 'table');
    return table?.tagName === 'TABLE' ? (table as HTMLTableElement) : null;
};

export const getNearestAnchor = (node: Node | null): HTMLAnchorElement | null => {
    const anchor = closestFromNode(node, 'a');
    return anchor?.tagName === 'A' ? (anchor as HTMLAnchorElement) : null;
};

export const normalizeColor = (value: string): string | null => {
    const trimmed = value.trim();
    if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed.toLowerCase();
    if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
        return `#${trimmed
            .slice(1)
            .split('')
            .map((character) => `${character}${character}`)
            .join('')}`.toLowerCase();
    }

    const rgb = trimmed.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (!rgb) return null;

    return `#${[rgb[1], rgb[2], rgb[3]].map((valuePart) => Number(valuePart).toString(16).padStart(2, '0')).join('')}`;
};

export const normalizeFontSize = (value: string): string | undefined => {
    const px = value.trim().match(/^(\d+(?:\.\d+)?)px$/i);
    if (!px) return undefined;
    return String(Math.round(Number(px[1])));
};

export const readFontSize = (document: Document, node: Node | null): string | undefined => {
    let current = elementFromSelectionNode(node) as HTMLElement | null;
    if (!current) return undefined;

    while (current) {
        const inline = normalizeFontSize(current.style?.fontSize ?? '');
        if (inline) return inline;
        current = current.parentElement;
    }

    const element = elementFromSelectionNode(node) as HTMLElement | null;
    if (!element) return undefined;
    return normalizeFontSize(document.defaultView?.getComputedStyle(element).fontSize ?? '');
};

export const normalizeFontFamily = (value: string): string | undefined => {
    const primary = value.split(',')[0];
    if (!primary) return undefined;
    return FONT_FAMILY_BY_PRIMARY.get(normalizeFamilyToken(primary));
};

export const readFontFamily = (document: Document, node: Node | null): string | undefined => {
    const element = elementFromSelectionNode(node) as HTMLElement | null;
    if (!element) return undefined;

    const inline = normalizeFontFamily(element.style?.fontFamily ?? '');
    if (inline) return inline;

    return normalizeFontFamily(document.defaultView?.getComputedStyle(element).fontFamily ?? '');
};

const isTableCell = (element: Element): boolean => element.tagName === 'TD' || element.tagName === 'TH';

const CELL_BORDER_STYLE_PROP: Record<VisualEditorCellEdge, 'borderTop' | 'borderRight' | 'borderBottom' | 'borderLeft'> = {
    top: 'borderTop',
    right: 'borderRight',
    bottom: 'borderBottom',
    left: 'borderLeft'
};

const CELL_BORDER_COLOR_PROP: Record<VisualEditorCellEdge, 'borderTopColor' | 'borderRightColor' | 'borderBottomColor' | 'borderLeftColor'> = {
    top: 'borderTopColor',
    right: 'borderRightColor',
    bottom: 'borderBottomColor',
    left: 'borderLeftColor'
};

const isVisibleBorder = (value: string): boolean => {
    const normalized = value.trim().toLowerCase();
    return normalized !== '' && normalized !== 'none' && normalized !== '0' && !normalized.startsWith('0px');
};

export const readCellBorderEdges = (cell: HTMLElement | null): VisualEditorCellEdge[] => {
    if (!cell) return [];
    if (isVisibleBorder(cell.style.border)) return [...CELL_BORDER_EDGES];
    return CELL_BORDER_EDGES.filter((edge) => isVisibleBorder(cell.style[CELL_BORDER_STYLE_PROP[edge]]));
};

export const readCellBorderColor = (cell: HTMLElement | null): string | undefined => {
    if (!cell) return undefined;
    for (const edge of CELL_BORDER_EDGES) {
        const color = normalizeColor(cell.style[CELL_BORDER_COLOR_PROP[edge]]);
        if (color) return color;
    }
    const fromColor = normalizeColor(cell.style.borderColor);
    if (fromColor) return fromColor;
    const match = cell.style.border.match(/#(?:[0-9a-f]{3,8})\b|rgba?\([^)]+\)/i);
    return match ? (normalizeColor(match[0]) ?? undefined) : undefined;
};

export const readCellFill = (cell: HTMLElement | null): string | undefined => {
    if (!cell) return undefined;
    return normalizeColor(cell.style.backgroundColor || cell.style.background) ?? undefined;
};

/** Inline highlight only — skip cell backgrounds so fill is not treated as text highlight. */
export const readHighlight = (node: Node | null): string | undefined => {
    let current: Element | null = elementFromSelectionNode(node);

    while (current && !isTableCell(current)) {
        const inline = normalizeColor((current as HTMLElement).style?.backgroundColor ?? '');
        if (inline) return inline;
        current = current.parentElement;
    }

    return undefined;
};

export const readAlign = (node: Node | null): VisualEditorAlign | undefined => {
    let current: Element | null = elementFromSelectionNode(node);

    while (current) {
        const inline = (current as HTMLElement).style?.textAlign?.toLowerCase();
        if (inline && ALIGN_VALUES.has(inline as VisualEditorAlign)) return inline as VisualEditorAlign;

        const attr = current.getAttribute('align')?.toLowerCase();
        if (attr && ALIGN_VALUES.has(attr as VisualEditorAlign)) return attr as VisualEditorAlign;

        current = current.parentElement;
    }

    return undefined;
};

export const readActiveLink = (node: Node | null): VisualEditorActiveLink | undefined => {
    const anchor = getNearestAnchor(node);
    if (!anchor) return undefined;

    return {
        href: anchor.getAttribute('href') ?? '',
        text: anchor.textContent ?? '',
        target: anchor.getAttribute('target')
    };
};

const hasInlineFormat = (node: Node | null, tags: string): boolean => Boolean(closestFromNode(node, tags));

export const readBold = (node: Node | null): boolean => hasInlineFormat(node, 'strong, b');
export const readItalic = (node: Node | null): boolean => hasInlineFormat(node, 'em, i');
export const readUnderline = (node: Node | null): boolean => hasInlineFormat(node, 'u');

export const readHeading = (node: Node | null): HtmlEditorHeading => {
    const heading = closestFromNode(node, 'h1, h2, h3, h4, h5, h6');
    const tag = heading?.tagName.toLowerCase();
    if (tag && (HTML_EDITOR_HEADINGS as readonly string[]).includes(tag)) {
        return tag as HtmlEditorHeading;
    }
    return 'text';
};

export const readSelectionStyle = (document: Document, node: Node | null): VisualEditorSelectionStyle => {
    const element = elementFromSelectionNode(node);
    const computedColor = element ? normalizeColor(document.defaultView?.getComputedStyle(element).color ?? '') : null;
    const cell = closestFromNode(node, 'td, th') as HTMLElement | null;
    const cellElement = cell && isTableCell(cell) ? cell : null;
    const cellBorderEdges = readCellBorderEdges(cellElement);
    const cellBorderColor = readCellBorderColor(cellElement);
    const cellFillColor = readCellFill(cellElement);

    return {
        color: computedColor ?? undefined,
        backgroundColor: readHighlight(node),
        fontSize: readFontSize(document, node),
        fontFamily: readFontFamily(document, node),
        bold: readBold(node),
        italic: readItalic(node),
        underline: readUnderline(node),
        heading: readHeading(node),
        inTable: Boolean(getNearestTable(node)),
        cellBorderEdges: cellBorderEdges.length > 0 ? cellBorderEdges : undefined,
        cellBorderColor,
        cellFillColor,
        activeLink: readActiveLink(node),
        align: readAlign(node)
    };
};
